/** The OAuth providers offered for primary-account sign-in, plus the
 *  per-provider data the shared flow needs.
 *
 *  This registry is the "express it as data" answer the callback route
 *  predicted when it noted that a second provider would fork the
 *  Google-hardcoded restart dispatch. Everything that genuinely varies
 *  between providers lives HERE; every other module takes the provider as
 *  a parameter and looks its differences up, so adding a third provider is
 *  one entry rather than a grep for "google".
 *
 *  Apple is not optional decoration: App Review guideline 4.8 requires an
 *  app offering a third-party login (our Google button) to also offer a
 *  login service that limits collection to name + email, lets users keep
 *  their email private, and doesn't collect app interactions for ads.
 *  Email/password does NOT satisfy it — it fails the private-email clause,
 *  which is why our 1.0 (2) submission was rejected on 4.8 despite having
 *  one. Sign in with Apple is the service Apple names as qualifying.
 *
 *  Plain data only, no JSX: this module is imported by Server Actions, and
 *  the provider icons are client components. The icon mapping lives with
 *  the button that renders it. */

export const OAUTH_PROVIDERS = {
  google: {
    /** Rendered as "<verb> with Google". */
    name: "Google",
    /** Explicit account picker every time — including on the auto-retry,
     *  where silently reusing the landing context's default Google session
     *  could log the user into a DIFFERENT account than the one they picked
     *  on the first attempt (personal vs school). */
    authorizeParams: { prompt: "select_account" },
  },
  apple: {
    name: "Apple",
    /** Deliberately empty, and NOT a copy of Google's. Apple's /authorize
     *  rejects unknown parameters, and `prompt=select_account` is Google
     *  vocabulary. Scopes and `response_mode=form_post` (which Apple
     *  REQUIRES once name/email scopes are requested) are set by GoTrue's
     *  own Apple provider — passing our own would fight it. */
    authorizeParams: undefined,
  },
} as const satisfies Record<string, { name: string; authorizeParams?: Record<string, string> }>;

export type OAuthProvider = keyof typeof OAUTH_PROVIDERS;

/** Shape of the per-route Server Action that starts a provider flow. Named
 *  once so the form, the button block, and each route's action map can't
 *  drift on the signature. */
export type OAuthFormAction = (formData: FormData) => Promise<void>;

/** Narrow the callback URL's `provider` marker to a provider we actually
 *  offer. Returns false for null (email-link exchanges carry no marker) and
 *  for any unknown value, which is what keeps the auto-retry from
 *  restarting a flow we can't build params for. */
export function isOAuthProvider(value: string | null | undefined): value is OAuthProvider {
  return typeof value === "string" && Object.hasOwn(OAUTH_PROVIDERS, value);
}
